import { colors } from '@/constants/colors';
import React from 'react';
import styled from 'styled-components';
import Basic from './ShopTheme/Basic';
import ModernTheme from './ShopTheme/ModernTheme';
import DarkTheme from './ShopTheme/DarkTheme';
import GamingTheme from './ShopTheme/GamingTheme';
import ShopeeTheme from './ShopTheme/ShopeeTheme';
import ProfessionalTheme from './ShopTheme/ProfessionalTheme';

interface Props {
   designTheme: string;
   slidesEnabled?: boolean;
   slidesHeight?: number;
   storeName?: string;
   storeLocation?: string;
   storeLogo?: string;
}

const themeAccents: Record<string, string> = {
   basic: '#1D1D1F',
   modern: '#3760F9',
   dark: '#a78bfa',
   gaming: '#00e676',
   shopee: '#ee4d2d',
   professional: '#0f172a',
};

const themeConfig: Record<string, { bg: string; nameColor: string; descColor: string }> = {
   basic: { bg: 'white', nameColor: '#1D1D1F', descColor: '#999' },
   modern: { bg: '#f8fafc', nameColor: '#0f172a', descColor: '#64748b' },
   dark: { bg: '#0f0f17', nameColor: '#e2e8f0', descColor: '#64748b' },
   gaming: { bg: '#0a0a0f', nameColor: '#e0e0e0', descColor: '#555' },
   shopee: { bg: '#fff5f2', nameColor: '#222', descColor: '#888' },
   professional: { bg: '#f0f4f8', nameColor: '#0f172a', descColor: '#475569' },
};

const ShopTheme = ({ designTheme, slidesEnabled = true, slidesHeight = 200, storeName, storeLocation, storeLogo }: Props) => {
   const config = themeConfig[designTheme] || themeConfig.basic;
   const accent = themeAccents[designTheme] || themeAccents.basic;

   const renderTheme = () => {
      switch (designTheme) {
         case 'modern':
            return <ModernTheme colorTheme={accent} />;
         case 'dark':
            return <DarkTheme colorTheme={accent} />;
         case 'gaming':
            return <GamingTheme colorTheme={accent} />;
         case 'shopee':
            return <ShopeeTheme colorTheme={accent} />;
         case 'professional':
            return <ProfessionalTheme colorTheme={accent} />;
         default:
            return <Basic colorTheme={accent} />;
      }
   };

   // Scale the slidesHeight for the mini preview (real 120-400px → preview 40-130px)
   const previewSlideHeight = Math.round(40 + ((slidesHeight - 120) / (400 - 120)) * 90);

   return (
      <Wrapper>
         <PhoneBg $bg={config.bg}>
            <ShopHeader>
               {storeLogo && (
                  <PreviewLogo src={storeLogo} alt="" />
               )}
               <ShopHeaderText>
                  <ShopName $color={config.nameColor}>{storeName || 'Vendo Universe'}</ShopName>
                  <ShopDescription $color={config.descColor}>
                     {storeLocation || 'Apple St. Obrero, Manila'}
                  </ShopDescription>
               </ShopHeaderText>
            </ShopHeader>
            {slidesEnabled && (
               <ThemeImgSlider $height={previewSlideHeight}>
                  <ThemeImgSliderItem>
                     <ThemeImg src={`/slide1.svg`} alt="slide" />
                  </ThemeImgSliderItem>
               </ThemeImgSlider>
            )}
            <Content>{renderTheme()}</Content>
         </PhoneBg>
      </Wrapper>
   );
};

const Wrapper = styled.div`
   margin-top: 1rem;
   background-image: url('/themes/iphone.svg');
   background-size: contain;
   background-position: center;
   background-repeat: no-repeat;
   height: 500px;
   overflow: hidden;
   text-align: left;
`;

const PhoneBg = styled.div<{ $bg: string }>`
   background: ${({ $bg }) => $bg};
   margin-top: 14px;
   margin-left: 18px;
   margin-right: 18px;
   height: calc(100% - 28px);
   overflow: hidden;
   border-radius: 20px;
   display: flex;
   flex-direction: column;
`;

const ShopHeader = styled.div`
   padding: 4px 7px;
   display: flex;
   align-items: center;
   gap: 6px;
`;

const PreviewLogo = styled.img`
   width: 24px;
   height: 24px;
   border-radius: 6px;
   object-fit: cover;
   flex-shrink: 0;
`;

const ShopHeaderText = styled.div`
   text-align: left;
`;

const ShopName = styled.p<{ $color: string }>`
   font-size: 16px;
   color: ${({ $color }) => $color};
`;

const ShopDescription = styled.p<{ $color: string }>`
   font-size: 12px;
   color: ${({ $color }) => $color};
`;

const ThemeImg = styled.img`
   width: 100%;
   height: 100%;
   object-fit: cover;
`;

const ThemeImgSlider = styled.div<{ $height: number }>`
   padding: 0 4px;
   width: 100%;
   height: ${({ $height }) => $height}px;
   overflow: hidden;
`;

const ThemeImgSliderItem = styled.div`
   width: 100%;
   height: 100%;
`;

const Content = styled.div`
   margin-left: 0;
   margin-right: 0;
   padding: 0 7px;
   overflow-y: auto;
   flex: 1;
   padding-bottom: 2rem;
   scrollbar-width: none;
   &::-webkit-scrollbar {
      display: none;
   }
`;

export default ShopTheme;
